import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { CreateMachineRequest } from '../../types/machine';

export interface MachineFormValues extends CreateMachineRequest {
  status: NonNullable<CreateMachineRequest['status']>;
  location: NonNullable<CreateMachineRequest['location']>;
}

interface MachineFormProps {
  title: string;
  initialValues: MachineFormValues;
  onSubmit: (values: MachineFormValues) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel: string;
  cancelLabel?: string;
  error?: string | null;
}

type ValidationErrors = Partial<Record<keyof MachineFormValues, string>>;

const defaultValidationErrors: ValidationErrors = {};

export const MachineForm: React.FC<MachineFormProps> = ({
  title,
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel,
  cancelLabel = 'Abbrechen',
  error,
}) => {
  const [values, setValues] = useState<MachineFormValues>(initialValues);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(defaultValidationErrors);

  // Ensure form re-initialises when new values are provided (e.g. when edit data finishes loading)
  useEffect(() => {
    setValues(initialValues);
    setValidationErrors(defaultValidationErrors);
  }, [initialValues]);

  const isDirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(initialValues), [initialValues, values]);

  const handleChange = (field: keyof MachineFormValues) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: string } }
  ) => {
    const value = event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));

    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const errors: ValidationErrors = {};

    if (!values.name?.trim()) {
      errors.name = 'Maschinenname ist erforderlich';
    }

    if (!values.location?.trim()) {
      errors.location = 'Standort ist erforderlich';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) {
      return;
    }

    await onSubmit({ ...values, name: values.name.trim(), location: values.location.trim() });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {cancelLabel}
          </Button>
          <Typography variant="h4" component="h1">
            {title}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Maschinenname *"
                value={values.name}
                onChange={handleChange('name')}
                error={Boolean(validationErrors.name)}
                helperText={validationErrors.name}
                disabled={isSubmitting}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={values.status}
                  onChange={handleChange('status')}
                  disabled={isSubmitting}
                >
                  <MenuItem value="active">Aktiv</MenuItem>
                  <MenuItem value="maintenance">Wartung</MenuItem>
                  <MenuItem value="inactive">Inaktiv</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Standort *"
                value={values.location}
                onChange={handleChange('location')}
                error={Boolean(validationErrors.location)}
                helperText={validationErrors.location}
                disabled={isSubmitting}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Beschreibung"
                value={values.description}
                onChange={handleChange('description')}
                disabled={isSubmitting}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  {cancelLabel}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={isSubmitting || !isDirty}
                >
                  {isSubmitting ? 'Wird gespeichert…' : submitLabel}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

